import LoadingState from "Components/organisms/loading-state";
import { create } from "lodash";
import UnAuthorizedPage from "pages/UnAuthorized";
import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { PermissionsSliceState, setScreens } from "slices/auth/permissions/reducer";
import { setOrgContext, setUser } from "slices/auth/profile/reducer";
import { HelpDocumentSliceState } from "slices/help-documents/reducer";
import { getHelpDocuments } from "slices/help-documents/thunk";


export const DASHBOARD_PATH = '/dashboard';
export const UNAUTHORIZED_PAGE = '/unauthorized';
export const PROFILE_PAGE = '/profile';
// Open to every user; the API filters each manual by the user's screens
export const MANUALS_PAGE = '/manuals';
// Open to every user (only memos already sent to everyone). Not in the menu yet: reachable by URL only.
export const VERSION_LOG_PAGE = '/version-log';

interface AuthProtectedProps {
  children: ReactNode;
}

const selectPermissions = createSelector(
  (state: any) => state.Permissions,
  ({ screens }: PermissionsSliceState) => ({
    screens
  })
);


const selectHelpDocuments = createSelector(
  (state: any) => state.HelpDocuments,
  ({ helpDocuments }: HelpDocumentSliceState) => ({
    helpDocuments
  })
)

const AuthProtected = ({ children }: AuthProtectedProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch() as any;
  const { screens } = useSelector(selectPermissions);
  const { helpDocuments } = useSelector(selectHelpDocuments);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const checkAuthStatus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/check-auth`, {
        credentials: "include",
      });

      if (res.ok) {
        const { data } = await res.json();
        dispatch(setScreens(data.screens));
        dispatch(setOrgContext({
          jobTitle: data.jobTitle,
          department: data.department,
          employee: data.employee,
          roles: data.roles,
        }));
        dispatch(setUser(data.user));
        setIsLoggedIn(true);
        return;
      }
      setIsLoggedIn(false);
      navigate("/login");
    } catch (error: any) {
      navigate("/login");
    }
  };

  // 🔒 Authorization check
  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (screens.length > 0) {
      const currentPath = location.pathname;

      const hasAccess =
        location.pathname === DASHBOARD_PATH ||
        location.pathname === UNAUTHORIZED_PAGE ||
        location.pathname === PROFILE_PAGE ||
        location.pathname === MANUALS_PAGE ||
        location.pathname.startsWith(`${MANUALS_PAGE}/`) ||
        location.pathname === VERSION_LOG_PAGE ||
        screens.some((screen) => currentPath.includes(screen.url))

      if (!hasAccess && currentPath !== "/unauthorized") {
        navigate("/unauthorized");
      }
    }
  }, [screens, location.pathname]);



  // Get Help Documents
  useEffect(() => {
    if (screens.length > 0 && Object.keys(helpDocuments).length === 0) {
      dispatch(getHelpDocuments())
    }
  }, [screens])

  return <>{isLoggedIn ? children : <LoadingState title="Authenticating" subtitle="Please wait while authenticate you" />}</>;

};

export default AuthProtected;
